import { useEffect, useState } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { ArrowLeft, ArrowRight, MailCheck } from "lucide-react";
import Logo from "@/components/Logo";
import { useAuth, errorMessage } from "@/lib/auth";
import "@/auth.css";

export default function Auth() {
  const { user, loading, requestLink } = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  if (loading) return <div className="route-loading" aria-busy="true" />;
  if (user) return <Navigate to={user.onboarded ? (location.state?.from || "/dashboard") : "/onboarding"} replace />;

  const submit = async e => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await requestLink(email);
      setSent(true);
      setCooldown(30);
    } catch (err) {
      setError(errorMessage(err, "Impossible d'envoyer le lien. Réessayez dans un instant."));
    } finally { setBusy(false); }
  };

  return (
    <div className="auth-screen">
      <div className="auth-card">
        <Link to="/" aria-label="Perform, accueil"><Logo /></Link>

        {sent ? (
          <>
            <div className="auth-sent-icon"><MailCheck size={22} /></div>
            <h1>Regardez votre boîte mail.</h1>
            <p className="auth-lead">
              Nous avons envoyé un lien de connexion à <strong>{email}</strong>.
              Il est valable 15 minutes et ne fonctionne qu'une fois.
            </p>
            <div className="auth-sent-actions">
              <button
                type="button" className="button button-outline" data-testid="auth-resend"
                disabled={cooldown > 0 || busy} onClick={submit}
              >
                {cooldown > 0 ? `Renvoyer dans ${cooldown} s` : "Renvoyer le lien"}
              </button>
              <button type="button" className="auth-link" data-testid="auth-change-email" onClick={() => { setSent(false); setError(""); }}>
                <ArrowLeft size={13} />Changer d'adresse
              </button>
            </div>
            {error && <div role="alert" className="form-error" data-testid="auth-error">{error}</div>}
            <p className="auth-fineprint">
              Rien reçu ? Vérifiez vos indésirables. Aucun compte n'est créé tant que le lien n'est pas ouvert.
            </p>
          </>
        ) : (
          <>
            <h1>Votre progression commence ici.</h1>
            <p className="auth-lead">
              Entrez votre adresse e-mail : nous vous envoyons un lien de connexion.
              Pas de mot de passe à retenir.
            </p>
            <form onSubmit={submit}>
              <label htmlFor="auth-email">Adresse e-mail</label>
              <input
                id="auth-email" data-testid="auth-email" type="email" required autoFocus
                autoComplete="email" placeholder="vous@exemple.fr"
                value={email} onChange={e => setEmail(e.target.value)}
              />
              {error && <div role="alert" className="form-error" data-testid="auth-error">{error}</div>}
              <button className="button button-dark" type="submit" disabled={busy || !email} data-testid="auth-submit">
                {busy ? "Envoi…" : "Recevoir mon lien"}<ArrowRight size={15} />
              </button>
            </form>
            <p className="auth-fineprint">
              En continuant, vous acceptez nos <Link to="/legal/cgu">conditions générales</Link> et
              notre <Link to="/legal/confidentialite">politique de confidentialité</Link>.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
